import { memo, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import "react-calendar/dist/Calendar.css";

import { SearchButton } from "@/components/common/buttons";
import { LocationDropdown, CalendarInput, TrailerTypeInput, RadiusInput } from "@/components/search";
import { cn } from "@/utils/css";
import { swapInputs, clearInputs, setPickup, setPickupQuery, setDelivery, setDeliveryQuery } from "@/state/searchInputSlice";
import { clearCurrentSearch } from "@/state/currentSearchSlice";
import { useQuery } from "@/utils/query";

export const SearchInputs = memo(({ className = "", isLocked }) => {
    const { searchType, pickup, delivery, pickupQuery, deliveryQuery } = useSelector((state) => state.searchInput);
    const {
        pickupZip,
        pickupPlaceName,
        pickupStateCode,
        pickupWithZip,
        deliveryZip,
        deliveryPlaceName,
        deliveryStateCode,
        deliveryWithZip,
    } = useQuery();
    const isMarketPotential = searchType === "market_potential";
    const isLoadRate = searchType === "load_rates";
    const isLoadboard = searchType === "loadboard";
    const dispatch = useDispatch();
    const hasDelivery = isLoadboard || isLoadRate;

    const searchBtnRef = useRef(null);

    const handleEnterKey = useCallback((e) => {
        if (e.key !== "Enter" || e.defaultPrevented) return;
        const btn = searchBtnRef.current?.querySelector("button:not([disabled])");
        btn?.click();
    }, []);

    const handleSwitch = () => {
        // Swap pickup/delivery and dim the content below until the user searches again.
        dispatch(swapInputs());
    };

    useEffect(() => {
        return () => {
            dispatch(clearInputs());
            dispatch(clearCurrentSearch());
        };
    }, []);

    return (
        <div
            className={cn("w-full md:justify-center min-h-12 flex flex-col md:flex-row mt-4 p-6 pt-0 md:p-0 md:pb-4", className)}
            onKeyDown={handleEnterKey}
        >
            <TrailerTypeInput disabled={isLocked} />
            <LocationDropdown
                className={cn("transition-minWidth ease-in-out duration-50 w-full md:max-w-[200px]", {
                    "mb-1 md:mb-0 md:mr-0": hasDelivery,
                    "lg:max-w-[264px]": isLoadRate,
                })}
                label={hasDelivery ? "Pickup" : "Location"}
                width={isLoadRate ? 264 : 200}
                handleSwitch={isLoadRate ? handleSwitch : undefined}
                name="pickup"
                value={pickup}
                setValue={(value) => dispatch(setPickup(value))}
                query={pickupQuery}
                setQuery={(query) => dispatch(setPickupQuery(query))}
                urlQueryValues={{
                    zip: pickupZip,
                    placeName: pickupPlaceName,
                    stateCode: pickupStateCode,
                    withZip: pickupWithZip,
                }}
                disabled={isLocked}
            />
            <RadiusInput
                type="pickup"
                visible={isMarketPotential || isLoadboard}
                className={cn({
                    "mr-1": isMarketPotential,
                })}
                handleSwitch={isLoadboard ? handleSwitch : undefined}
                disabled={isLocked}
            />
            <LocationDropdown
                className={cn("transition-all ease-in-out duration-50 w-full h-[64px]", {
                    "md:max-w-[200px] mb-1 md:mb-0 overflow-visible": hasDelivery,
                    "lg:max-w-[264px] md:mr-1": isLoadRate,
                    "min-w-0 max-w-0 overflow-hidden max-h-0": !hasDelivery,
                })}
                label="Delivery"
                width={isLoadRate ? 264 : isMarketPotential ? 0 : 200}
                name="delivery"
                value={delivery}
                setValue={(value) => dispatch(setDelivery(value))}
                query={deliveryQuery}
                setQuery={(query) => dispatch(setDeliveryQuery(query))}
                urlQueryValues={{
                    zip: deliveryZip,
                    placeName: deliveryPlaceName,
                    stateCode: deliveryStateCode,
                    withZip: deliveryWithZip,
                }}
                disabled={isLocked}
            />
            <RadiusInput
                type="delivery"
                visible={isLoadboard}
                className={cn({
                    "mr-1": isLoadboard,
                    "opacity-0 invisible": !isLoadboard && isLocked,
                })}
                disabled={isLocked}
            />
            <CalendarInput name="pickup" isLastInput={!isLoadboard} disabled={isLocked} />
            <CalendarInput
                name="delivery"
                isLastInput
                className={cn("transition-all ease-in-out duration-50", {
                    "md:max-w-[0px] overflow-hidden opacity-0": !isLoadboard,
                    "md:max-w-[200px] overflow-visible opacity-100": isLoadboard,
                })}
                disabled={isLocked}
            />
            <div ref={searchBtnRef} className="relative w-full md:w-[78px] flex md:ml-4">
                <SearchButton />
            </div>
        </div>
    );
});
